import React, { useEffect, useState } from 'react';
import {
  Users, UserPlus, Users2, Briefcase, ShieldCheck, CalendarClock,
} from 'lucide-react';
import { agencyAPI, agencyEnablementAPI, agencyAppointmentAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';

/**
 * AgencySnapshot — dashboard-scoped operational snapshot card for the
 * logged-in agency. Renders six executive-signal cells:
 *   1. Total Agents         — every agent in the downline.
 *   2. Direct Agents        — agents attached to THIS agency (not sub-agencies).
 *   3. Downline Agents      — total minus direct.
 *   4. Enabled Carriers     — carriers this agency is enabled to request.
 *   5. Active Appointments  — appointments with status='Active' AND writing_number
 *                             populated (matches the canonical taxonomy rule
 *                             used across Agency Profile and Contracting Health).
 *   6. Last Updated         — most recent modification timestamp on the
 *                             agency record.
 *
 * Data sources (agency-scoped and all pre-existing):
 *   • GET /agencies/{id}                       — for `updated_at` timestamp.
 *   • GET /agencies/{id}/summary               — total_agents, direct_agents.
 *   • GET /agencies/{id}/enabled-carriers      — enabled carrier list.
 *   • GET /agencies/{id}/appointments          — agency-level appointments.
 *
 * Deliberately excludes compensation levels, production, and payout signals
 * per Phase 20.5 guardrails.
 */

const Cell = ({ label, value, hint, icon: Icon, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    {Icon && (
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-600">
        <Icon size={17} />
      </div>
    )}
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="text-sm font-semibold mt-0.5 text-slate-900 truncate">
        {value ?? '—'}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  </div>
);

// KR treats an Approved carrier appointment as Active (Approved = Active).
// Accept both statuses so this snapshot agrees with Agent Profile / Admin.
const countActiveWithWritingNumbers = (appts) =>
  (Array.isArray(appts) ? appts : []).filter(
    (a) => (a?.appointment_status === 'Active' || a?.appointment_status === 'Approved') && a?.writing_number,
  ).length;

export const AgencySnapshot = ({ agencyId }) => {
  const [agency, setAgency] = useState(null);
  const [summary, setSummary] = useState(null);
  const [enabledCount, setEnabledCount] = useState(null);
  const [activeAppointments, setActiveAppointments] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agencyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [agencyRes, summaryRes, enabledRes, apptsRes] = await Promise.all([
          agencyAPI.get(agencyId).catch(() => null),
          agencyAPI.getSummary(agencyId).catch(() => null),
          agencyEnablementAPI.getEnabledCarriers(agencyId).catch(() => null),
          agencyAppointmentAPI.list(agencyId).catch(() => null),
        ]);
        if (cancelled) return;
        setAgency(agencyRes?.data || null);
        setSummary(summaryRes?.data || null);
        setEnabledCount(Array.isArray(enabledRes?.data) ? enabledRes.data.length : 0);
        setActiveAppointments(countActiveWithWritingNumbers(apptsRes?.data));
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load agency snapshot:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agencyId]);

  const totalAgents = summary?.total_agents ?? 0;
  const directAgents = summary?.direct_agents ?? 0;
  const downlineAgents = Math.max(0, totalAgents - directAgents);
  const lastUpdated = agency?.updated_at || agency?.created_at;

  return (
    <section className="mb-8" data-testid="agency-snapshot">
      <div className="mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Agency Snapshot
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Operational structure of your agency — team size, carrier footprint,
          and appointment status.
        </p>
      </div>

      <Card className="animate-slideIn">
        <CardContent className="p-5">
          {loading ? (
            <div className="py-6 text-center text-sm text-slate-400" data-testid="agency-snapshot-loading">
              Loading snapshot…
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-5">
              <Cell label="Total Agents" value={totalAgents} icon={Users} testId="agency-snapshot-total-agents" />
              <Cell label="Direct Agents" value={directAgents} icon={UserPlus} testId="agency-snapshot-direct-agents"
                hint={directAgents ? 'Attached to this agency' : null} />
              <Cell label="Downline Agents" value={downlineAgents} icon={Users2} testId="agency-snapshot-downline-agents"
                hint={downlineAgents ? 'Across sub-agencies' : null} />
              <Cell label="Enabled Carriers" value={enabledCount ?? '—'} icon={Briefcase} testId="agency-snapshot-enabled-carriers"
                hint="Carriers you may request" />
              <Cell label="Active Appointments" value={activeAppointments ?? '—'} icon={ShieldCheck} testId="agency-snapshot-active-appointments"
                hint="Writing numbers you hold" />
              <Cell label="Last Updated" value={lastUpdated ? formatDate(lastUpdated) : '—'} icon={CalendarClock} testId="agency-snapshot-last-updated" />
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default AgencySnapshot;
